/**
 * Timbrica — 3d-photo-to-model turntable reconstruction.
 *
 * Exposes window.PhotoScanner with:
 *   runTurntable(frames, opts)  — silhouette visual-hull carving + marching cubes
 *
 * Frames: [{image: HTMLImageElement, name: string}]
 * Assumed camera model: orthographic, subject rotating on a vertical Y axis,
 * angles evenly distributed across 360° in frame order.
 */
(function (global) {
    'use strict';

    // ─── Background detection + silhouette segmentation ─────────────────────
    // Build a binary foreground mask from an RGBA image. bgMode ∈
    // {'white', 'green', 'auto'}. threshold in 0..255 colour-distance units.
    //
    // One reused scratch canvas for every segmentation pass. A fresh canvas
    // per call (16-36 frames per run, plus one per threshold-slider tick for
    // the preview) drains iOS Safari's per-page canvas memory pool — huge
    // source photos make each drawImage decode expensive too — and once an
    // allocation fails the context is invalid and getImageData throws
    // InvalidStateError (TIMBRICA-FRONTEND-CT).
    var segCanvas = null;
    function segment(image, mask, w, h, bgMode, threshold) {
        // Validate BEFORE touching the canvas: a broken / not-yet-decoded /
        // memory-evicted source reports 0×0, and drawImage+getImageData on a
        // 0-sized surface is the other iOS InvalidStateError path. Throwing a
        // plain Error keeps the module's existing error route: runTurntable()
        // catches it and skips the frame (zero good frames → the localized
        // empty-mesh message), the blade's silhouette preview hides itself.
        var iw = (image && (image.naturalWidth !== undefined ? image.naturalWidth
                 : (image.videoWidth !== undefined ? image.videoWidth : image.width))) || 0;
        var ih = (image && (image.naturalHeight !== undefined ? image.naturalHeight
                 : (image.videoHeight !== undefined ? image.videoHeight : image.height))) || 0;
        if (!(w > 0) || !(h > 0) || !(iw > 0) || !(ih > 0)) {
            throw new Error('segment: unusable source image (' + iw + 'x' + ih + ' -> ' + w + 'x' + h + ')');
        }
        if (!segCanvas) segCanvas = document.createElement('canvas');
        var canvas = segCanvas;
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('segment: 2d context unavailable');
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(image, 0, 0, w, h);
        var data = ctx.getImageData(0, 0, w, h).data;

        var bg;
        if (bgMode === 'white') bg = [245, 245, 245];
        else if (bgMode === 'green') bg = [70, 200, 90];
        else bg = autoDetectBg(data, w, h);

        var thresholdSq = threshold * threshold;
        for (var p = 0, i = 0; i < data.length; i += 4, p++) {
            var dr = data[i]     - bg[0];
            var dg = data[i + 1] - bg[1];
            var db = data[i + 2] - bg[2];
            var d2 = dr * dr + dg * dg + db * db;
            mask[p] = d2 > thresholdSq ? 1 : 0;
        }
        // Morphological erosion + dilation to clean up noise (salt).
        // One iteration is enough for most backgrounds.
        erodeMask(mask, w, h);
        dilateMask(mask, w, h);
        // Largest connected component — drops isolated specks.
        keepLargestComponent(mask, w, h);
    }

    // Sample the four corners + edge midpoints; take the median RGB as the
    // background colour. This is more robust to shadows and vignetting than
    // averaging.
    function autoDetectBg(data, w, h) {
        var samples = [];
        function pick(x, y) {
            var i = (y * w + x) * 4;
            samples.push([data[i], data[i+1], data[i+2]]);
        }
        var M = 8; // sample an 8-pixel region at each anchor
        for (var dy = 0; dy < M; dy++) for (var dx = 0; dx < M; dx++) {
            pick(dx, dy);
            pick(w - 1 - dx, dy);
            pick(dx, h - 1 - dy);
            pick(w - 1 - dx, h - 1 - dy);
            pick(Math.floor(w/2) - dx, dy);
            pick(Math.floor(w/2) - dx, h - 1 - dy);
        }
        // Median over each channel.
        return [0, 1, 2].map(function (c) {
            var arr = samples.map(function (s) { return s[c]; }).sort(function (a, b) { return a - b; });
            return arr[Math.floor(arr.length / 2)];
        });
    }

    function erodeMask(mask, w, h) {
        var src = new Uint8Array(mask);
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                var p = y * w + x;
                if (src[p] === 0) { mask[p] = 0; continue; }
                if (x === 0 || y === 0 || x === w - 1 || y === h - 1) { mask[p] = 0; continue; }
                mask[p] = src[p-1] & src[p+1] & src[p-w] & src[p+w];
            }
        }
    }
    function dilateMask(mask, w, h) {
        var src = new Uint8Array(mask);
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                var p = y * w + x;
                if (src[p] === 1) { mask[p] = 1; continue; }
                if (x === 0 || y === 0 || x === w - 1 || y === h - 1) continue;
                mask[p] = src[p-1] | src[p+1] | src[p-w] | src[p+w];
            }
        }
    }

    // BFS-based connected-component scan; drop everything except the largest
    // region. Fixes floating debris / small shadows that survive threshold.
    function keepLargestComponent(mask, w, h) {
        var labels = new Int32Array(w * h);
        var queue = [];
        var counts = [0]; // label 0 = background
        var best = 0, bestLabel = 0;
        var nextLabel = 0;
        for (var y0 = 0; y0 < h; y0++) {
            for (var x0 = 0; x0 < w; x0++) {
                var idx0 = y0 * w + x0;
                if (mask[idx0] !== 1 || labels[idx0] !== 0) continue;
                nextLabel++;
                queue.length = 0;
                queue.push(idx0);
                labels[idx0] = nextLabel;
                var count = 0;
                while (queue.length) {
                    var p = queue.pop();
                    count++;
                    var px = p % w, py = (p / w) | 0;
                    if (px > 0   && mask[p-1]     === 1 && labels[p-1]     === 0) { labels[p-1]     = nextLabel; queue.push(p - 1); }
                    if (px < w-1 && mask[p+1]     === 1 && labels[p+1]     === 0) { labels[p+1]     = nextLabel; queue.push(p + 1); }
                    if (py > 0   && mask[p-w]     === 1 && labels[p-w]     === 0) { labels[p-w]     = nextLabel; queue.push(p - w); }
                    if (py < h-1 && mask[p+w]     === 1 && labels[p+w]     === 0) { labels[p+w]     = nextLabel; queue.push(p + w); }
                }
                counts[nextLabel] = count;
                if (count > best) { best = count; bestLabel = nextLabel; }
            }
        }
        if (!bestLabel) return; // silhouette empty
        for (var i = 0; i < mask.length; i++) {
            mask[i] = labels[i] === bestLabel ? 1 : 0;
        }
    }

    // ─── Visual hull voxel carving ──────────────────────────────────────────
    // Project each voxel into each silhouette; keep voxel only if it's inside
    // the mask for ALL photos. Camera model: orthographic projection, subject
    // centred at origin, rotating on Y axis, one photo per evenly-spaced angle.
    function carveHull(silhouettes, voxN, imgW, imgH) {
        var N = silhouettes.length;
        // Start filled
        var grid = new Uint8Array(voxN * voxN * voxN);
        for (var i = 0; i < grid.length; i++) grid[i] = 1;

        // Subject fits in the central 80% of the frame (safer margin for
        // edge-clipped photos). Unit voxel grid spans [-1,1]³.
        var fitFactor = 0.8;
        var uScale = (imgW / 2) * fitFactor;
        var vScale = (imgH / 2) * fitFactor;

        for (var s = 0; s < N; s++) {
            var mask = silhouettes[s];
            var angle = (s / N) * Math.PI * 2;
            var cosA = Math.cos(angle), sinA = Math.sin(angle);
            for (var vz = 0; vz < voxN; vz++) {
                var z = (vz / (voxN - 1)) * 2 - 1;
                for (var vy = 0; vy < voxN; vy++) {
                    var y = (vy / (voxN - 1)) * 2 - 1;
                    for (var vx = 0; vx < voxN; vx++) {
                        var gIdx = (vz * voxN + vy) * voxN + vx;
                        if (!grid[gIdx]) continue;
                        var x = (vx / (voxN - 1)) * 2 - 1;
                        // Rotate the voxel *back* into the frame's coordinates.
                        var xr = x * cosA - z * sinA;
                        // Orthographic projection onto image plane.
                        var u = Math.round(xr * uScale + imgW / 2);
                        var v = Math.round(-y * vScale + imgH / 2);
                        if (u < 0 || u >= imgW || v < 0 || v >= imgH) { grid[gIdx] = 0; continue; }
                        if (mask[v * imgW + u] === 0) grid[gIdx] = 0;
                    }
                }
            }
        }
        return grid;
    }

    // ─── Marching cubes ─────────────────────────────────────────────────────
    // Classic 256-entry lookup tables (Paul Bourke / NVIDIA SDK public domain).
    var EDGE_TABLE = new Uint16Array([
        0x0  ,0x109,0x203,0x30a,0x406,0x50f,0x605,0x70c,0x80c,0x905,0xa0f,0xb06,0xc0a,0xd03,0xe09,0xf00,
        0x190,0x99 ,0x393,0x29a,0x596,0x49f,0x795,0x69c,0x99c,0x895,0xb9f,0xa96,0xd9a,0xc93,0xf99,0xe90,
        0x230,0x339,0x33 ,0x13a,0x636,0x73f,0x435,0x53c,0xa3c,0xb35,0x83f,0x936,0xe3a,0xf33,0xc39,0xd30,
        0x3a0,0x2a9,0x1a3,0xaa ,0x7a6,0x6af,0x5a5,0x4ac,0xbac,0xaa5,0x9af,0x8a6,0xfaa,0xea3,0xda9,0xca0,
        0x460,0x569,0x663,0x76a,0x66 ,0x16f,0x265,0x36c,0xc6c,0xd65,0xe6f,0xf66,0x86a,0x963,0xa69,0xb60,
        0x5f0,0x4f9,0x7f3,0x6fa,0x1f6,0xff ,0x3f5,0x2fc,0xdfc,0xcf5,0xfff,0xef6,0x9fa,0x8f3,0xbf9,0xaf0,
        0x650,0x759,0x453,0x55a,0x256,0x35f,0x55 ,0x15c,0xe5c,0xf55,0xc5f,0xd56,0xa5a,0xb53,0x859,0x950,
        0x7c0,0x6c9,0x5c3,0x4ca,0x3c6,0x2cf,0x1c5,0xcc ,0xfcc,0xec5,0xdcf,0xcc6,0xbca,0xac3,0x9c9,0x8c0,
        0x8c0,0x9c9,0xac3,0xbca,0xcc6,0xdcf,0xec5,0xfcc,0xcc ,0x1c5,0x2cf,0x3c6,0x4ca,0x5c3,0x6c9,0x7c0,
        0x950,0x859,0xb53,0xa5a,0xd56,0xc5f,0xf55,0xe5c,0x15c,0x55 ,0x35f,0x256,0x55a,0x453,0x759,0x650,
        0xaf0,0xbf9,0x8f3,0x9fa,0xef6,0xfff,0xcf5,0xdfc,0x2fc,0x3f5,0xff ,0x1f6,0x6fa,0x7f3,0x4f9,0x5f0,
        0xb60,0xa69,0x963,0x86a,0xf66,0xe6f,0xd65,0xc6c,0x36c,0x265,0x16f,0x66 ,0x76a,0x663,0x569,0x460,
        0xca0,0xda9,0xea3,0xfaa,0x8a6,0x9af,0xaa5,0xbac,0x4ac,0x5a5,0x6af,0x7a6,0xaa ,0x1a3,0x2a9,0x3a0,
        0xd30,0xc39,0xf33,0xe3a,0x936,0x83f,0xb35,0xa3c,0x53c,0x435,0x73f,0x636,0x13a,0x33 ,0x339,0x230,
        0xe90,0xf99,0xc93,0xd9a,0xa96,0xb9f,0x895,0x99c,0x69c,0x795,0x49f,0x596,0x29a,0x393,0x99 ,0x190,
        0xf00,0xe09,0xd03,0xc0a,0xb06,0xa0f,0x905,0x80c,0x70c,0x605,0x50f,0x406,0x30a,0x203,0x109,0x0
    ]);

    // TRI_TABLE as Int8Array — -1 marks end of list for a given configuration.
    // Source: http://paulbourke.net/geometry/polygonise/
    var TRI_TABLE = new Int8Array([
        -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 1, 9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 8, 3, 9, 8, 1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3, 1, 2,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9, 2,10, 0, 2, 9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         2, 8, 3, 2,10, 8,10, 9, 8,-1,-1,-1,-1,-1,-1,-1,
         3,11, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0,11, 2, 8,11, 0,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 9, 0, 2, 3,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1,11, 2, 1, 9,11, 9, 8,11,-1,-1,-1,-1,-1,-1,-1,
         3,10, 1,11,10, 3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0,10, 1, 0, 8,10, 8,11,10,-1,-1,-1,-1,-1,-1,-1,
         3, 9, 0, 3,11, 9,11,10, 9,-1,-1,-1,-1,-1,-1,-1,
         9, 8,10,10, 8,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 7, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 3, 0, 7, 3, 4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 1, 9, 8, 4, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 1, 9, 4, 7, 1, 7, 3, 1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10, 8, 4, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 4, 7, 3, 0, 4, 1, 2,10,-1,-1,-1,-1,-1,-1,-1,
         9, 2,10, 9, 0, 2, 8, 4, 7,-1,-1,-1,-1,-1,-1,-1,
         2,10, 9, 2, 9, 7, 2, 7, 3, 7, 9, 4,-1,-1,-1,-1,
         8, 4, 7, 3,11, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        11, 4, 7,11, 2, 4, 2, 0, 4,-1,-1,-1,-1,-1,-1,-1,
         9, 0, 1, 8, 4, 7, 2, 3,11,-1,-1,-1,-1,-1,-1,-1,
         4, 7,11, 9, 4,11, 9,11, 2, 9, 2, 1,-1,-1,-1,-1,
         3,10, 1, 3,11,10, 7, 8, 4,-1,-1,-1,-1,-1,-1,-1,
         1,11,10, 1, 4,11, 1, 0, 4, 7,11, 4,-1,-1,-1,-1,
         4, 7, 8, 9, 0,11, 9,11,10,11, 0, 3,-1,-1,-1,-1,
         4, 7,11, 4,11, 9, 9,11,10,-1,-1,-1,-1,-1,-1,-1,
         9, 5, 4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9, 5, 4, 0, 8, 3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 5, 4, 1, 5, 0,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         8, 5, 4, 8, 3, 5, 3, 1, 5,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10, 9, 5, 4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 0, 8, 1, 2,10, 4, 9, 5,-1,-1,-1,-1,-1,-1,-1,
         5, 2,10, 5, 4, 2, 4, 0, 2,-1,-1,-1,-1,-1,-1,-1,
         2,10, 5, 3, 2, 5, 3, 5, 4, 3, 4, 8,-1,-1,-1,-1,
         9, 5, 4, 2, 3,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0,11, 2, 0, 8,11, 4, 9, 5,-1,-1,-1,-1,-1,-1,-1,
         0, 5, 4, 0, 1, 5, 2, 3,11,-1,-1,-1,-1,-1,-1,-1,
         2, 1, 5, 2, 5, 8, 2, 8,11, 4, 8, 5,-1,-1,-1,-1,
        10, 3,11,10, 1, 3, 9, 5, 4,-1,-1,-1,-1,-1,-1,-1,
         4, 9, 5, 0, 8, 1, 8,10, 1, 8,11,10,-1,-1,-1,-1,
         5, 4, 0, 5, 0,11, 5,11,10,11, 0, 3,-1,-1,-1,-1,
         5, 4, 8, 5, 8,10,10, 8,11,-1,-1,-1,-1,-1,-1,-1,
         9, 7, 8, 5, 7, 9,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9, 3, 0, 9, 5, 3, 5, 7, 3,-1,-1,-1,-1,-1,-1,-1,
         0, 7, 8, 0, 1, 7, 1, 5, 7,-1,-1,-1,-1,-1,-1,-1,
         1, 5, 3, 3, 5, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9, 7, 8, 9, 5, 7,10, 1, 2,-1,-1,-1,-1,-1,-1,-1,
        10, 1, 2, 9, 5, 0, 5, 3, 0, 5, 7, 3,-1,-1,-1,-1,
         8, 0, 2, 8, 2, 5, 8, 5, 7,10, 5, 2,-1,-1,-1,-1,
         2,10, 5, 2, 5, 3, 3, 5, 7,-1,-1,-1,-1,-1,-1,-1,
         7, 9, 5, 7, 8, 9, 3,11, 2,-1,-1,-1,-1,-1,-1,-1,
         9, 5, 7, 9, 7, 2, 9, 2, 0, 2, 7,11,-1,-1,-1,-1,
         2, 3,11, 0, 1, 8, 1, 7, 8, 1, 5, 7,-1,-1,-1,-1,
        11, 2, 1,11, 1, 7, 7, 1, 5,-1,-1,-1,-1,-1,-1,-1,
         9, 5, 8, 8, 5, 7,10, 1, 3,10, 3,11,-1,-1,-1,-1,
         5, 7, 0, 5, 0, 9, 7,11, 0, 1, 0,10,11,10, 0,-1,
        11,10, 0,11, 0, 3,10, 5, 0, 8, 0, 7, 5, 7, 0,-1,
        11,10, 5, 7,11, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        10, 6, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3, 5,10, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9, 0, 1, 5,10, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 8, 3, 1, 9, 8, 5,10, 6,-1,-1,-1,-1,-1,-1,-1,
         1, 6, 5, 2, 6, 1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 6, 5, 1, 2, 6, 3, 0, 8,-1,-1,-1,-1,-1,-1,-1,
         9, 6, 5, 9, 0, 6, 0, 2, 6,-1,-1,-1,-1,-1,-1,-1,
         5, 9, 8, 5, 8, 2, 5, 2, 6, 3, 2, 8,-1,-1,-1,-1,
         2, 3,11,10, 6, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        11, 0, 8,11, 2, 0,10, 6, 5,-1,-1,-1,-1,-1,-1,-1,
         0, 1, 9, 2, 3,11, 5,10, 6,-1,-1,-1,-1,-1,-1,-1,
         5,10, 6, 1, 9, 2, 9,11, 2, 9, 8,11,-1,-1,-1,-1,
         6, 3,11, 6, 5, 3, 5, 1, 3,-1,-1,-1,-1,-1,-1,-1,
         0, 8,11, 0,11, 5, 0, 5, 1, 5,11, 6,-1,-1,-1,-1,
         3,11, 6, 0, 3, 6, 0, 6, 5, 0, 5, 9,-1,-1,-1,-1,
         6, 5, 9, 6, 9,11,11, 9, 8,-1,-1,-1,-1,-1,-1,-1,
         5,10, 6, 4, 7, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 3, 0, 4, 7, 3, 6, 5,10,-1,-1,-1,-1,-1,-1,-1,
         1, 9, 0, 5,10, 6, 8, 4, 7,-1,-1,-1,-1,-1,-1,-1,
        10, 6, 5, 1, 9, 7, 1, 7, 3, 7, 9, 4,-1,-1,-1,-1,
         6, 1, 2, 6, 5, 1, 4, 7, 8,-1,-1,-1,-1,-1,-1,-1,
         1, 2, 5, 5, 2, 6, 3, 0, 4, 3, 4, 7,-1,-1,-1,-1,
         8, 4, 7, 9, 0, 5, 0, 6, 5, 0, 2, 6,-1,-1,-1,-1,
         7, 3, 9, 7, 9, 4, 3, 2, 9, 5, 9, 6, 2, 6, 9,-1,
         3,11, 2, 7, 8, 4,10, 6, 5,-1,-1,-1,-1,-1,-1,-1,
         5,10, 6, 4, 7, 2, 4, 2, 0, 2, 7,11,-1,-1,-1,-1,
         0, 1, 9, 4, 7, 8, 2, 3,11, 5,10, 6,-1,-1,-1,-1,
         9, 2, 1, 9,11, 2, 9, 4,11, 7,11, 4, 5,10, 6,-1,
         8, 4, 7, 3,11, 5, 3, 5, 1, 5,11, 6,-1,-1,-1,-1,
         5, 1,11, 5,11, 6, 1, 0,11, 7,11, 4, 0, 4,11,-1,
         0, 5, 9, 0, 6, 5, 0, 3, 6,11, 6, 3, 8, 4, 7,-1,
         6, 5, 9, 6, 9,11, 4, 7, 9, 7,11, 9,-1,-1,-1,-1,
        10, 4, 9, 6, 4,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4,10, 6, 4, 9,10, 0, 8, 3,-1,-1,-1,-1,-1,-1,-1,
        10, 0, 1,10, 6, 0, 6, 4, 0,-1,-1,-1,-1,-1,-1,-1,
         8, 3, 1, 8, 1, 6, 8, 6, 4, 6, 1,10,-1,-1,-1,-1,
         1, 4, 9, 1, 2, 4, 2, 6, 4,-1,-1,-1,-1,-1,-1,-1,
         3, 0, 8, 1, 2, 9, 2, 4, 9, 2, 6, 4,-1,-1,-1,-1,
         0, 2, 4, 4, 2, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         8, 3, 2, 8, 2, 4, 4, 2, 6,-1,-1,-1,-1,-1,-1,-1,
        10, 4, 9,10, 6, 4,11, 2, 3,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 2, 2, 8,11, 4, 9,10, 4,10, 6,-1,-1,-1,-1,
         3,11, 2, 0, 1, 6, 0, 6, 4, 6, 1,10,-1,-1,-1,-1,
         6, 4, 1, 6, 1,10, 4, 8, 1, 2, 1,11, 8,11, 1,-1,
         9, 6, 4, 9, 3, 6, 9, 1, 3,11, 6, 3,-1,-1,-1,-1,
         8,11, 1, 8, 1, 0,11, 6, 1, 9, 1, 4, 6, 4, 1,-1,
         3,11, 6, 3, 6, 0, 0, 6, 4,-1,-1,-1,-1,-1,-1,-1,
         6, 4, 8,11, 6, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         7,10, 6, 7, 8,10, 8, 9,10,-1,-1,-1,-1,-1,-1,-1,
         0, 7, 3, 0,10, 7, 0, 9,10, 6, 7,10,-1,-1,-1,-1,
        10, 6, 7, 1,10, 7, 1, 7, 8, 1, 8, 0,-1,-1,-1,-1,
        10, 6, 7,10, 7, 1, 1, 7, 3,-1,-1,-1,-1,-1,-1,-1,
         1, 2, 6, 1, 6, 8, 1, 8, 9, 8, 6, 7,-1,-1,-1,-1,
         2, 6, 9, 2, 9, 1, 6, 7, 9, 0, 9, 3, 7, 3, 9,-1,
         7, 8, 0, 7, 0, 6, 6, 0, 2,-1,-1,-1,-1,-1,-1,-1,
         7, 3, 2, 6, 7, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         2, 3,11,10, 6, 8,10, 8, 9, 8, 6, 7,-1,-1,-1,-1,
         2, 0, 7, 2, 7,11, 0, 9, 7, 6, 7,10, 9,10, 7,-1,
         1, 8, 0, 1, 7, 8, 1,10, 7, 6, 7,10, 2, 3,11,-1,
        11, 2, 1,11, 1, 7,10, 6, 1, 6, 7, 1,-1,-1,-1,-1,
         8, 9, 6, 8, 6, 7, 9, 1, 6,11, 6, 3, 1, 3, 6,-1,
         0, 9, 1,11, 6, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         7, 8, 0, 7, 0, 6, 3,11, 0,11, 6, 0,-1,-1,-1,-1,
         7,11, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         7, 6,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 0, 8,11, 7, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 1, 9,11, 7, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         8, 1, 9, 8, 3, 1,11, 7, 6,-1,-1,-1,-1,-1,-1,-1,
        10, 1, 2, 6,11, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10, 3, 0, 8, 6,11, 7,-1,-1,-1,-1,-1,-1,-1,
         2, 9, 0, 2,10, 9, 6,11, 7,-1,-1,-1,-1,-1,-1,-1,
         6,11, 7, 2,10, 3,10, 8, 3,10, 9, 8,-1,-1,-1,-1,
         7, 2, 3, 6, 2, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         7, 0, 8, 7, 6, 0, 6, 2, 0,-1,-1,-1,-1,-1,-1,-1,
         2, 7, 6, 2, 3, 7, 0, 1, 9,-1,-1,-1,-1,-1,-1,-1,
         1, 6, 2, 1, 8, 6, 1, 9, 8, 8, 7, 6,-1,-1,-1,-1,
        10, 7, 6,10, 1, 7, 1, 3, 7,-1,-1,-1,-1,-1,-1,-1,
        10, 7, 6, 1, 7,10, 1, 8, 7, 1, 0, 8,-1,-1,-1,-1,
         0, 3, 7, 0, 7,10, 0,10, 9, 6,10, 7,-1,-1,-1,-1,
         7, 6,10, 7,10, 8, 8,10, 9,-1,-1,-1,-1,-1,-1,-1,
         6, 8, 4,11, 8, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 6,11, 3, 0, 6, 0, 4, 6,-1,-1,-1,-1,-1,-1,-1,
         8, 6,11, 8, 4, 6, 9, 0, 1,-1,-1,-1,-1,-1,-1,-1,
         9, 4, 6, 9, 6, 3, 9, 3, 1,11, 3, 6,-1,-1,-1,-1,
         6, 8, 4, 6,11, 8, 2,10, 1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10, 3, 0,11, 0, 6,11, 0, 4, 6,-1,-1,-1,-1,
         4,11, 8, 4, 6,11, 0, 2, 9, 2,10, 9,-1,-1,-1,-1,
        10, 9, 3,10, 3, 2, 9, 4, 3,11, 3, 6, 4, 6, 3,-1,
         8, 2, 3, 8, 4, 2, 4, 6, 2,-1,-1,-1,-1,-1,-1,-1,
         0, 4, 2, 4, 6, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 9, 0, 2, 3, 4, 2, 4, 6, 4, 3, 8,-1,-1,-1,-1,
         1, 9, 4, 1, 4, 2, 2, 4, 6,-1,-1,-1,-1,-1,-1,-1,
         8, 1, 3, 8, 6, 1, 8, 4, 6, 6,10, 1,-1,-1,-1,-1,
        10, 1, 0,10, 0, 6, 6, 0, 4,-1,-1,-1,-1,-1,-1,-1,
         4, 6, 3, 4, 3, 8, 6,10, 3, 0, 3, 9,10, 9, 3,-1,
        10, 9, 4, 6,10, 4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 9, 5, 7, 6,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3, 4, 9, 5,11, 7, 6,-1,-1,-1,-1,-1,-1,-1,
         5, 0, 1, 5, 4, 0, 7, 6,11,-1,-1,-1,-1,-1,-1,-1,
        11, 7, 6, 8, 3, 4, 3, 5, 4, 3, 1, 5,-1,-1,-1,-1,
         9, 5, 4,10, 1, 2, 7, 6,11,-1,-1,-1,-1,-1,-1,-1,
         6,11, 7, 1, 2,10, 0, 8, 3, 4, 9, 5,-1,-1,-1,-1,
         7, 6,11, 5, 4,10, 4, 2,10, 4, 0, 2,-1,-1,-1,-1,
         3, 4, 8, 3, 5, 4, 3, 2, 5,10, 5, 2,11, 7, 6,-1,
         7, 2, 3, 7, 6, 2, 5, 4, 9,-1,-1,-1,-1,-1,-1,-1,
         9, 5, 4, 0, 8, 6, 0, 6, 2, 6, 8, 7,-1,-1,-1,-1,
         3, 6, 2, 3, 7, 6, 1, 5, 0, 5, 4, 0,-1,-1,-1,-1,
         6, 2, 8, 6, 8, 7, 2, 1, 8, 4, 8, 5, 1, 5, 8,-1,
         9, 5, 4,10, 1, 6, 1, 7, 6, 1, 3, 7,-1,-1,-1,-1,
         1, 6,10, 1, 7, 6, 1, 0, 7, 8, 7, 0, 9, 5, 4,-1,
         4, 0,10, 4,10, 5, 0, 3,10, 6,10, 7, 3, 7,10,-1,
         7, 6,10, 7,10, 8, 5, 4,10, 4, 8,10,-1,-1,-1,-1,
         6, 9, 5, 6,11, 9,11, 8, 9,-1,-1,-1,-1,-1,-1,-1,
         3, 6,11, 0, 6, 3, 0, 5, 6, 0, 9, 5,-1,-1,-1,-1,
         0,11, 8, 0, 5,11, 0, 1, 5, 5, 6,11,-1,-1,-1,-1,
         6,11, 3, 6, 3, 5, 5, 3, 1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,10, 9, 5,11, 9,11, 8,11, 5, 6,-1,-1,-1,-1,
         0,11, 3, 0, 6,11, 0, 9, 6, 5, 6, 9, 1, 2,10,-1,
        11, 8, 5,11, 5, 6, 8, 0, 5,10, 5, 2, 0, 2, 5,-1,
         6,11, 3, 6, 3, 5, 2,10, 3,10, 5, 3,-1,-1,-1,-1,
         5, 8, 9, 5, 2, 8, 5, 6, 2, 3, 8, 2,-1,-1,-1,-1,
         9, 5, 6, 9, 6, 0, 0, 6, 2,-1,-1,-1,-1,-1,-1,-1,
         1, 5, 8, 1, 8, 0, 5, 6, 8, 3, 8, 2, 6, 2, 8,-1,
         1, 5, 6, 2, 1, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 3, 6, 1, 6,10, 3, 8, 6, 5, 6, 9, 8, 9, 6,-1,
        10, 1, 0,10, 0, 6, 9, 5, 0, 5, 6, 0,-1,-1,-1,-1,
         0, 3, 8, 5, 6,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        10, 5, 6,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        11, 5,10, 7, 5,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        11, 5,10,11, 7, 5, 8, 3, 0,-1,-1,-1,-1,-1,-1,-1,
         5,11, 7, 5,10,11, 1, 9, 0,-1,-1,-1,-1,-1,-1,-1,
        10, 7, 5,10,11, 7, 9, 8, 1, 8, 3, 1,-1,-1,-1,-1,
        11, 1, 2,11, 7, 1, 7, 5, 1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3, 1, 2, 7, 1, 7, 5, 7, 2,11,-1,-1,-1,-1,
         9, 7, 5, 9, 2, 7, 9, 0, 2, 2,11, 7,-1,-1,-1,-1,
         7, 5, 2, 7, 2,11, 5, 9, 2, 3, 2, 8, 9, 8, 2,-1,
         2, 5,10, 2, 3, 5, 3, 7, 5,-1,-1,-1,-1,-1,-1,-1,
         8, 2, 0, 8, 5, 2, 8, 7, 5,10, 2, 5,-1,-1,-1,-1,
         9, 0, 1, 5,10, 3, 5, 3, 7, 3,10, 2,-1,-1,-1,-1,
         9, 8, 2, 9, 2, 1, 8, 7, 2,10, 2, 5, 7, 5, 2,-1,
         1, 3, 5, 3, 7, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 7, 0, 7, 1, 1, 7, 5,-1,-1,-1,-1,-1,-1,-1,
         9, 0, 3, 9, 3, 5, 5, 3, 7,-1,-1,-1,-1,-1,-1,-1,
         9, 8, 7, 5, 9, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         5, 8, 4, 5,10, 8,10,11, 8,-1,-1,-1,-1,-1,-1,-1,
         5, 0, 4, 5,11, 0, 5,10,11,11, 3, 0,-1,-1,-1,-1,
         0, 1, 9, 8, 4,10, 8,10,11,10, 4, 5,-1,-1,-1,-1,
        10,11, 4,10, 4, 5,11, 3, 4, 9, 4, 1, 3, 1, 4,-1,
         2, 5, 1, 2, 8, 5, 2,11, 8, 4, 5, 8,-1,-1,-1,-1,
         0, 4,11, 0,11, 3, 4, 5,11, 2,11, 1, 5, 1,11,-1,
         0, 2, 5, 0, 5, 9, 2,11, 5, 4, 5, 8,11, 8, 5,-1,
         9, 4, 5, 2,11, 3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         2, 5,10, 3, 5, 2, 3, 4, 5, 3, 8, 4,-1,-1,-1,-1,
         5,10, 2, 5, 2, 4, 4, 2, 0,-1,-1,-1,-1,-1,-1,-1,
         3,10, 2, 3, 5,10, 3, 8, 5, 4, 5, 8, 0, 1, 9,-1,
         5,10, 2, 5, 2, 4, 1, 9, 2, 9, 4, 2,-1,-1,-1,-1,
         8, 4, 5, 8, 5, 3, 3, 5, 1,-1,-1,-1,-1,-1,-1,-1,
         0, 4, 5, 1, 0, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         8, 4, 5, 8, 5, 3, 9, 0, 5, 0, 3, 5,-1,-1,-1,-1,
         9, 4, 5,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4,11, 7, 4, 9,11, 9,10,11,-1,-1,-1,-1,-1,-1,-1,
         0, 8, 3, 4, 9, 7, 9,11, 7, 9,10,11,-1,-1,-1,-1,
         1,10,11, 1,11, 4, 1, 4, 0, 7, 4,11,-1,-1,-1,-1,
         3, 1, 4, 3, 4, 8, 1,10, 4, 7, 4,11,10,11, 4,-1,
         4,11, 7, 9,11, 4, 9, 2,11, 9, 1, 2,-1,-1,-1,-1,
         9, 7, 4, 9,11, 7, 9, 1,11, 2,11, 1, 0, 8, 3,-1,
        11, 7, 4,11, 4, 2, 2, 4, 0,-1,-1,-1,-1,-1,-1,-1,
        11, 7, 4,11, 4, 2, 8, 3, 4, 3, 2, 4,-1,-1,-1,-1,
         2, 9,10, 2, 7, 9, 2, 3, 7, 7, 4, 9,-1,-1,-1,-1,
         9,10, 7, 9, 7, 4,10, 2, 7, 8, 7, 0, 2, 0, 7,-1,
         3, 7,10, 3,10, 2, 7, 4,10, 1,10, 0, 4, 0,10,-1,
         1,10, 2, 8, 7, 4,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 9, 1, 4, 1, 7, 7, 1, 3,-1,-1,-1,-1,-1,-1,-1,
         4, 9, 1, 4, 1, 7, 0, 8, 1, 8, 7, 1,-1,-1,-1,-1,
         4, 0, 3, 7, 4, 3,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         4, 8, 7,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         9,10, 8,10,11, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 0, 9, 3, 9,11,11, 9,10,-1,-1,-1,-1,-1,-1,-1,
         0, 1,10, 0,10, 8, 8,10,11,-1,-1,-1,-1,-1,-1,-1,
         3, 1,10,11, 3,10,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 2,11, 1,11, 9, 9,11, 8,-1,-1,-1,-1,-1,-1,-1,
         3, 0, 9, 3, 9,11, 1, 2, 9, 2,11, 9,-1,-1,-1,-1,
         0, 2,11, 8, 0,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         3, 2,11,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         2, 3, 8, 2, 8,10,10, 8, 9,-1,-1,-1,-1,-1,-1,-1,
         9,10, 2, 0, 9, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         2, 3, 8, 2, 8,10, 0, 1, 8, 1,10, 8,-1,-1,-1,-1,
         1,10, 2,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         1, 3, 8, 9, 1, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 9, 1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
         0, 3, 8,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,
        -1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1,-1
    ]);

    // Vertex and edge offsets (see Paul Bourke reference diagram).
    // CUBE_VERTS[i] = [dx, dy, dz] of corner i relative to voxel (x,y,z).
    var CUBE_VERTS = [
        [0,0,0],[1,0,0],[1,1,0],[0,1,0],
        [0,0,1],[1,0,1],[1,1,1],[0,1,1]
    ];
    // Each edge connects corner A → corner B.
    var EDGE_CONNECT = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7]
    ];

    function marchingCubes(grid, N) {
        var positions = [];
        var iso = 0.5;
        var vertList = new Array(12);
        for (var i = 0; i < 12; i++) vertList[i] = [0, 0, 0];

        function idx(x, y, z) { return (z * N + y) * N + x; }
        function val(x, y, z) { return grid[idx(x, y, z)] ? 1.0 : 0.0; }

        for (var z = 0; z < N - 1; z++) {
            for (var y = 0; y < N - 1; y++) {
                for (var x = 0; x < N - 1; x++) {
                    var cubeVals = [
                        val(x,     y,     z    ),
                        val(x + 1, y,     z    ),
                        val(x + 1, y + 1, z    ),
                        val(x,     y + 1, z    ),
                        val(x,     y,     z + 1),
                        val(x + 1, y,     z + 1),
                        val(x + 1, y + 1, z + 1),
                        val(x,     y + 1, z + 1),
                    ];
                    var cubeIndex = 0;
                    for (var c = 0; c < 8; c++) {
                        if (cubeVals[c] > iso) cubeIndex |= (1 << c);
                    }
                    var edgeMask = EDGE_TABLE[cubeIndex];
                    if (edgeMask === 0) continue;
                    for (var e = 0; e < 12; e++) {
                        if (!(edgeMask & (1 << e))) continue;
                        var a = EDGE_CONNECT[e][0], b = EDGE_CONNECT[e][1];
                        var va = CUBE_VERTS[a], vb = CUBE_VERTS[b];
                        var valA = cubeVals[a], valB = cubeVals[b];
                        var t;
                        if (Math.abs(valB - valA) < 1e-6) t = 0.5;
                        else t = (iso - valA) / (valB - valA);
                        vertList[e][0] = x + va[0] + t * (vb[0] - va[0]);
                        vertList[e][1] = y + va[1] + t * (vb[1] - va[1]);
                        vertList[e][2] = z + va[2] + t * (vb[2] - va[2]);
                    }
                    var base = cubeIndex * 16;
                    for (var ti = 0; ti < 16; ti += 3) {
                        var a0 = TRI_TABLE[base + ti];
                        if (a0 === -1) break;
                        var a1 = TRI_TABLE[base + ti + 1];
                        var a2 = TRI_TABLE[base + ti + 2];
                        // Winding is CCW when viewed from outside (+iso side).
                        positions.push(
                            vertList[a0][0], vertList[a0][1], vertList[a0][2],
                            vertList[a1][0], vertList[a1][1], vertList[a1][2],
                            vertList[a2][0], vertList[a2][1], vertList[a2][2]
                        );
                    }
                }
            }
        }
        return new Float32Array(positions);
    }

    // Box-blur the grid as floats → gives marching cubes smooth surfaces
    // instead of staircased voxels.
    function blurGridFloat(grid, N) {
        var src = new Float32Array(grid.length);
        for (var i = 0; i < grid.length; i++) src[i] = grid[i];
        var dst = new Float32Array(grid.length);
        function idx(x, y, z) { return (z * N + y) * N + x; }
        for (var z = 0; z < N; z++) {
            for (var y = 0; y < N; y++) {
                for (var x = 0; x < N; x++) {
                    var sum = 0, cnt = 0;
                    for (var dz = -1; dz <= 1; dz++) for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
                        var nx = x + dx, ny = y + dy, nz = z + dz;
                        if (nx < 0 || ny < 0 || nz < 0 || nx >= N || ny >= N || nz >= N) continue;
                        sum += src[idx(nx, ny, nz)]; cnt++;
                    }
                    dst[idx(x, y, z)] = sum / cnt;
                }
            }
        }
        return dst;
    }

    // ─── Turntable pipeline ────────────────────────────────────────────────
    // frames: [{image, name}, …]; opts: { voxN, bgMode, threshold, targetMm, onProgress, messages }
    //
    // `messages` is an optional locale-aware bundle from the blade:
    //   { segmenting, carving, smoothing, marching, empty }
    //   segmenting may contain `:i` / `:n` placeholders replaced per step.
    function runTurntable(frames, opts) {
        return new Promise(function (resolve, reject) {
            try {
                var voxN = opts.voxN || 96;
                var bgMode = opts.bgMode || 'auto';
                var threshold = opts.threshold || 35;
                var targetMm = opts.targetMm || 80;
                var onProgress = opts.onProgress || function () {};
                var msg = opts.messages || {
                    segmenting: 'Segmenting :i/:n',
                    carving:    'Carving visual hull…',
                    smoothing:  'Smoothing…',
                    marching:   'Marching cubes…',
                    empty:      'Empty mesh — lower threshold or check background',
                };

                // Downscale each photo to a fixed size for silhouette extraction.
                // 256 is enough resolution to resolve the edge but keeps carving
                // fast (O(N³ × frames × 1) per voxel).
                var sW = 256, sH = 256;

                onProgress(0.05, msg.segmenting.replace(':i', '0').replace(':n', String(frames.length)));
                var silhouettes = [];
                var N = frames.length;

                // Process asynchronously so the UI doesn't freeze.
                var i = 0;
                function stepSegment() {
                    if (i >= N) return stepCarve();
                    var f = frames[i];
                    // A frame-extraction hole leaves frames[i] undefined — skip it
                    // instead of crashing on f.image (TIMBRICA-FRONTEND-A5).
                    if (!f || !f.image) { i++; setTimeout(stepSegment, 0); return; }
                    var mask = new Uint8Array(sW * sH);
                    // Same skip-don't-crash contract for a frame whose IMAGE is
                    // unusable: under memory pressure iOS drops canvas backing
                    // stores (the event's breadcrumbs show WebGL context-lost
                    // moments earlier) and drawImage/getImageData throw
                    // InvalidStateError (TIMBRICA-FRONTEND-CT). One bad frame
                    // must not kill the whole reconstruction; zero good frames
                    // falls through to the existing empty-mesh error UX.
                    try {
                        segment(f.image, mask, sW, sH, bgMode, threshold);
                    } catch (segErr) {
                        try { if (global.__ae) global.__ae('tool_error', { slug: '3d-photo-to-model', error_type: 'segment_frame_skipped', err: String(segErr && segErr.name || segErr).slice(0, 60), frame: i }); } catch (_) {}
                        i++; setTimeout(stepSegment, 0); return;
                    }
                    silhouettes.push(mask);
                    i++;
                    onProgress(0.05 + 0.25 * (i / N), msg.segmenting.replace(':i', String(i)).replace(':n', String(N)));
                    setTimeout(stepSegment, 0);
                }

                function stepCarve() {
                    // Every frame skipped (holes + unusable images) → carving
                    // with zero silhouettes would emit an uncarved solid block;
                    // surface the honest empty-mesh error instead.
                    if (!silhouettes.length) { return reject(new Error(msg.empty)); }
                    onProgress(0.35, msg.carving);
                    setTimeout(function () {
                        var grid = carveHull(silhouettes, voxN, sW, sH);
                        onProgress(0.75, msg.smoothing);
                        setTimeout(function () {
                            var smoothed = blurGridFloat(grid, voxN);
                            onProgress(0.85, msg.marching);
                            setTimeout(function () {
                                var positions = marchingCubes(smoothed, voxN);
                                if (positions.length === 0) {
                                    return reject(new Error(msg.empty));
                                }
                                // Scale from grid units [0,N-1] → centred, with
                                // longest side = targetMm.
                                var half = (voxN - 1) / 2;
                                // Figure extent along all three axes.
                                var min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
                                for (var p = 0; p < positions.length; p += 3) {
                                    if (positions[p]   < min[0]) min[0] = positions[p];
                                    if (positions[p]   > max[0]) max[0] = positions[p];
                                    if (positions[p+1] < min[1]) min[1] = positions[p+1];
                                    if (positions[p+1] > max[1]) max[1] = positions[p+1];
                                    if (positions[p+2] < min[2]) min[2] = positions[p+2];
                                    if (positions[p+2] > max[2]) max[2] = positions[p+2];
                                }
                                var ext = Math.max(max[0]-min[0], max[1]-min[1], max[2]-min[2]) || 1;
                                var scale = targetMm / ext;
                                // Re-orient: marching cubes outputs in a Z-up grid, but three.js uses Y-up.
                                // Swap Y↔Z so the object lands upright (original photos assume Y axis).
                                for (var q = 0; q < positions.length; q += 3) {
                                    var x = (positions[q] - half) * scale;
                                    var y = (positions[q+1] - half) * scale;
                                    var z = (positions[q+2] - half) * scale;
                                    positions[q]   = x;
                                    positions[q+1] = y;
                                    positions[q+2] = z;
                                }
                                var normals = computeFlatNormals(positions);
                                onProgress(1, 'Done');
                                resolve({ positions: positions, normals: normals });
                            }, 0);
                        }, 0);
                    }, 0);
                }

                setTimeout(stepSegment, 0);
            } catch (e) { reject(e); }
        });
    }

    function computeFlatNormals(positions) {
        var normals = new Float32Array(positions.length);
        for (var i = 0; i < positions.length; i += 9) {
            var ax = positions[i+3] - positions[i],   ay = positions[i+4] - positions[i+1], az = positions[i+5] - positions[i+2];
            var bx = positions[i+6] - positions[i],   by = positions[i+7] - positions[i+1], bz = positions[i+8] - positions[i+2];
            var nx = ay * bz - az * by;
            var ny = az * bx - ax * bz;
            var nz = ax * by - ay * bx;
            var len = Math.sqrt(nx*nx + ny*ny + nz*nz) || 1;
            nx /= len; ny /= len; nz /= len;
            for (var v = 0; v < 3; v++) {
                normals[i + v*3]     = nx;
                normals[i + v*3 + 1] = ny;
                normals[i + v*3 + 2] = nz;
            }
        }
        return normals;
    }

    global.PhotoScanner = {
        runTurntable: runTurntable,
        segment: segment,
        carveHull: carveHull,
        marchingCubes: marchingCubes,
    };
})(typeof window !== 'undefined' ? window : globalThis);
